import * as React from 'react';
import { FunctionComponent, ReactNode, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI
import { styled } from '@mui/material/styles';

// Custom components
import SidebarContext, { SidebarContextType } from '@/context/SidebarContext/SidebarContext';
import UserContext from '@/context/UserContext/UserContext';

// Types
import { Permission } from '@/@types/common';
import { getOptimalTextColorForBackgroundColor } from '@/lib/colorUtilities';

export interface ISidebarTabListItem {
  id: string;
  icon: ReactNode;
  disabled?: boolean;
  title: string;
  needPermission: Permission[];
  onChange?: (id: string) => void;
  onSidebarCollapse?: (newState: boolean) => void;
  disableUrlBehaviour?: boolean;
}

const StyledLi = styled('li')(({ theme }) => ({
  width: '100%',
  height: 40,
  color: getOptimalTextColorForBackgroundColor(theme.palette.primary.light),
  fontSize: '12pt',
  overflow: 'hidden',
  transition: 'all 80ms',
  ':hover': {
    color: getOptimalTextColorForBackgroundColor(theme.palette.primary.light),
    backgroundColor: theme.palette.primary.dark,
  },
}));

const DisabledLi = styled(StyledLi)(() => ({
  color: 'rgba(255, 255, 255, 0.4)',
  ':hover': {
    backgroundColor: 'transparent',
  },
}));

const ActiveLi = styled(StyledLi)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

const StyledA = styled('a')(() => ({
  display: 'block',
  width: '100%',
  height: '100%',
  lineHeight: '40px',
  color: 'inherit',
  textDecoration: 'none',
  textAlign: 'center',
}));

const DisabledA = styled(StyledA)(() => ({
  cursor: 'default',
}));

const SidebarTabListItem: FunctionComponent<ISidebarTabListItem> = (props) => {
  // const theme = useTheme();

  const location = useLocation();
  const hash = location.hash.replace(/^#/, '');

  const { t } = useTranslation();

  const { id, icon, disabled, title, onChange, needPermission, disableUrlBehaviour, onSidebarCollapse } = props;

  const sideBarContext = useContext(SidebarContext) as SidebarContextType;
  const userContext = useContext(UserContext);

  const handlePaneChange = (clickedId: string): void => {
    if (!disabled) {
      // userContext.setUserSetting({map_sidebar_pane: id});
      sideBarContext.setActivePane(clickedId);
      sideBarContext.setSidebarHeading(title);

      if (onChange) {
        onChange(clickedId);
      }
    }
  };

  React.useEffect(() => {
    if (sideBarContext.collapsed && sideBarContext.activePane === id && onSidebarCollapse) {
      onSidebarCollapse(sideBarContext.collapsed);
    }
  }, [sideBarContext.collapsed]);

  React.useEffect(() => {
    if (disableUrlBehaviour) return;
    if (!disabled && hash === id && sideBarContext.activePane !== id) {
      handlePaneChange(id);
    }
  }, [hash]);

  const isActive = sideBarContext.activePane === id;

  const classNames = [];
  if (disabled) {
    classNames.push('disabled');
  }
  if (isActive) {
    classNames.push('active');
  }
  const joinedClassNames = classNames.length > 0 ? classNames.join(' ') : '';

  const LI = disabled ? DisabledLi : isActive ? ActiveLi : StyledLi;
  const A = disabled ? DisabledA : StyledA;

  if (userContext?.hasAnyPermission(needPermission)) {
    return (
      <LI className={joinedClassNames} title={t(title) as string}>
        <A
          href={disableUrlBehaviour ? undefined : `#${id} `}
          role="tab"
          onClick={() => handlePaneChange(id)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {icon}
        </A>
      </LI>
    );
  }
  return null;
};

export default SidebarTabListItem;
